import { LuCheck, LuMinus, LuSearch, LuX } from 'react-icons/lu'

import { Badge, BadgeVariant } from '@/lib/core/components/Badge'
import { Button } from '@/lib/core/components/Button'
import { ModalBase } from '@/lib/core/components/Modal'
import { Truncate } from '@/lib/core/components/Truncate'

import { BulkTagsModalProps } from './BulkTagsModal.types'
import styles from './BulkTagsModal.module.css'
import { useBulkTagsModal } from './useBulkTagsModal'

export const BulkTagsModal = ({
  isOpen,
  selectedAssets,
  onClose,
  onApplySuccess,
}: BulkTagsModalProps) => {
  const {
    searchTerm,
    setSearchTerm,
    filteredTags,
    effectiveTagStates,
    pendingChanges,
    isApplying,
    applyError,
    networkError,
    overrideConfirmTag,
    overridePosition,
    handleTagClick,
    handleConfirmOverride,
    handleDismissOverride,
    handleApply,
    handleClose,
  } = useBulkTagsModal(selectedAssets, onApplySuccess)

  const handleModalClose = () => {
    handleClose()
    onClose()
  }

  return (
    <ModalBase isActive={isOpen} onEscape={handleModalClose} data-testid="bulk-tags-modal">
      <div className={styles.panel} style={{ position: 'relative' }}>
        <div className={styles.header}>
          <h2 className={styles.title} data-testid="bulk-tags-modal-title">
            Manage Tags for {selectedAssets.length} Asset{selectedAssets.length !== 1 ? 's' : ''}
          </h2>
          <div className={styles.headerActions}>
            <button className={styles.iconBtn} onClick={handleModalClose} aria-label="Close">
              <LuX size={18} />
            </button>
          </div>
        </div>

        <div className={styles.searchRow}>
          <div className={styles.searchWrap}>
            <LuSearch size={16} />
            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search tags…"
              aria-label="Search tags"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="bulk-tags-search"
            />
          </div>
        </div>

        <div className={styles.tagList} data-testid="bulk-tags-list">
          {filteredTags.length === 0 && (
            <p className={styles.emptyState}>No tags found</p>
          )}
          {filteredTags.map((tag) => {
            const state = effectiveTagStates.get(tag.id) ?? 'none'
            return (
              <div
                key={tag.id}
                className={styles.tagRow}
                onClick={(e) => handleTagClick(tag, e)}
                data-testid={`bulk-tag-row-${tag.id}`}
                role="checkbox"
                aria-checked={state === 'all' ? true : state === 'some' ? 'mixed' : false}
                aria-label={tag.name}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleTagClick(tag, e)
                  }
                }}
              >
                <div className={styles.checkbox} data-state={state}>
                  {state === 'all' && <LuCheck size={12} />}
                  {state === 'some' && <LuMinus size={12} />}
                </div>
                <Badge variant={BadgeVariant.TAG} backgroundColor={tag.color} minSize={20}>
                  <Truncate content={tag.name} limit={24} />
                </Badge>
              </div>
            )
          })}
        </div>

        <div className={styles.footer}>
          {(applyError ?? networkError) && (
            <p className={styles.applyError} data-testid="bulk-tags-apply-error">
              {applyError ?? networkError}
            </p>
          )}
          <div className={styles.footerActions}>
            <Button
              variant="ghost"
              size="medium"
              onClick={handleModalClose}
              data-testid="bulk-tags-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="medium"
              disabled={pendingChanges.size === 0 || isApplying || applyError !== null}
              onClick={handleApply}
              data-testid="bulk-tags-apply"
            >
              {isApplying ? 'Applying…' : 'Apply Changes'}
            </Button>
          </div>
        </div>

      </div>

      {overrideConfirmTag && overridePosition && (
        <>
          <div className={styles.overrideBackdrop} onClick={handleDismissOverride} />
          <div
            className={styles.overridePopout}
            style={{ top: overridePosition.y, left: overridePosition.x }}
            data-testid="bulk-tags-override-dialog"
          >
            <button
              className={styles.overrideItem}
              onClick={() => handleConfirmOverride('add')}
              data-testid="bulk-tags-override-confirm"
            >
              Apply to all
            </button>
            <button
              className={styles.overrideItem}
              onClick={() => handleConfirmOverride('remove')}
              data-testid="bulk-tags-override-remove"
            >
              Remove from all
            </button>
          </div>
        </>
      )}
    </ModalBase>
  )
}
